import type { InboxProvider } from "../api/connectionHub.types";
import { INBOX_PROVIDER } from "../api/connectionHub.types";

export interface ProviderSetupGuide {
  displayName: string;
  /** `**…**` marks the UI labels the steps panel renders in bold. */
  forwarding: string[];
  filter: string[];
  /** A caution shown above the steps. Only providers that need one set it. */
  notice?: string;
  /** Empty until the walkthroughs are recorded; an empty string hides the player. */
  forwardingVideoUrl: string;
  filterVideoUrl: string;
}

const OUTLOOK_RULE_STEPS = [
  "In Outlook, open **Settings** > **Mail** > **Rules**, then add a new rule.",
  "Fill in the name, condition and redirect action exactly as shown in the rule card.",
  "Uncheck **Stop processing more rules** and save the rule.",
];

const FORWARDING_ONLY_STEPS = [
  "Open your mail settings and find **Forwarding** (sometimes under **Rules** or **Filters**).",
  "Add the Merito address above as a forwarding address, and confirm it if your provider asks.",
  "Create a rule that forwards mail matching the **From** and **Has words** conditions above.",
  "Save it, then click **I’ve Set Up Forwarding**.",
];

const forwardingOnlyGuide = (displayName: string): ProviderSetupGuide => ({
  displayName,
  forwarding: FORWARDING_ONLY_STEPS,
  filter: [],
  forwardingVideoUrl: "",
  filterVideoUrl: "",
});

export const isForwardingOnlyProvider = (provider: InboxProvider): boolean =>
  provider !== INBOX_PROVIDER.GMAIL && provider !== INBOX_PROVIDER.OUTLOOK;

export const PROVIDER_SETUP_GUIDES: Partial<Record<InboxProvider, ProviderSetupGuide>> = {
  gmail: {
    displayName: "Gmail",
    forwarding: [
      "Lands on the General tab of Settings",
      "Go to Forwarding and POP/IMAP",
      "Click Add a forwarding address which opens a popup",
      "Paste {forwardingAddress}",
      "Click Next and click Proceed on the verification prompt",
      "Click OK to finish",
    ],
    filter: [
      "Gmail opens with the filter conditions already filled in",
      "Click Create filter",
      "Check Forward it to, then select {forwardingAddress} from the list",
      "Click Create filter again to confirm",
      "Google verifies it's you before the filter is finalised",
    ],
    forwardingVideoUrl: "",
    filterVideoUrl: "",
  },
  // Outlook is a single rule that matches and redirects, so both phases describe the same task.
  outlook: {
    displayName: "Outlook",
    forwarding: OUTLOOK_RULE_STEPS,
    filter: OUTLOOK_RULE_STEPS,
    notice:
      "**Heads up!** Make sure external email forwarding is allowed at your company. Many IT teams restrict this.",
    forwardingVideoUrl: "",
    filterVideoUrl: "",
  },
  zoho: forwardingOnlyGuide("Zoho Mail"),
  others: forwardingOnlyGuide("your mail provider"),
  unknown: forwardingOnlyGuide("your mail provider"),
};
